import { CaretRight, CircleNotch, Trash } from "@phosphor-icons/react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  type AdminHouseholdDetail,
  type Device,
  getApiErrorMessage,
  type Room,
  useUpdateHousehold,
  type Window,
} from "@/lib/api";
import { cn } from "@/lib/utils";

import { InlineEdit } from "./inline-edit";
import { MissingChip } from "./missing-chip";
import { RoomDevices } from "./room-devices";
import { RoomWindows } from "./room-windows";

/** A blank facade window — sensible defaults for a freshly added opening. */
const BLANK_WINDOW: Window = {
  window_type: "facade",
  area_m2: null,
  cardinal_orientation_deg: null,
  position_angle_deg: 0,
  is_openable: true,
  window_is_electric: false,
  has_blind: false,
  blind_is_electric: null,
  ha_device_id: null,
  ha_room_id: null,
};

interface RoomCardProps {
  household: AdminHouseholdDetail;
  roomIndex: number;
  defaultOpen?: boolean;
}

function floorLabel(floor: number | null | undefined): string {
  if (floor == null) {
    return "—";
  }
  if (floor === 0) {
    return "Ground";
  }
  const suffix = floor === 1 ? "st" : floor === 2 ? "nd" : floor === 3 ? "rd" : "th";
  return `${floor}${suffix} floor`;
}

function isDeviceLinked(device: Device): boolean {
  return Boolean(device.external_id || device.ha_device_id);
}

/** Numeric metric chip: a label under a bold, click-to-edit value. */
function MetricChip({
  value,
  suffix,
  label,
  onSave,
  ariaLabel,
}: {
  value: number | null | undefined;
  suffix?: string;
  label: string;
  onSave: (value: string) => Promise<void>;
  ariaLabel: string;
}) {
  return (
    <div className="bg-background flex items-center gap-1.5 rounded-sm px-2.5 py-1.5 text-[12.5px]">
      <InlineEdit
        value={value != null ? String(value) : ""}
        type="number"
        onSave={onSave}
        ariaLabel={ariaLabel}
        className="text-primary font-mono font-semibold"
      >
        <b className="text-primary font-mono font-semibold">
          {value ?? "—"}
          {value != null && suffix ? suffix : ""}
        </b>
      </InlineEdit>
      <span className="text-muted">{label}</span>
    </div>
  );
}

/**
 * Collapsible room accordion: summary row with readiness at a glance, and a
 * body of editable metric chips plus the windows/devices sub-lists. Every
 * field edit rebuilds the household's full `rooms` array (the backend
 * replaces it wholesale) and saves via `useUpdateHousehold`.
 */
export function RoomCard({ household, roomIndex, defaultOpen = false }: RoomCardProps) {
  const room = household.rooms[roomIndex];
  const updateMutation = useUpdateHousehold();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  if (!room) {
    return null;
  }

  const saveRoomPatch = async (patch: Partial<Room>) => {
    const nextRooms = household.rooms.map((r, i) => (i === roomIndex ? { ...r, ...patch } : r));
    await updateMutation.mutateAsync({ id: household.id, body: { rooms: nextRooms } });
  };

  const handleWindowChange = (windowIndex: number, patch: Partial<Window>) => {
    const nextWindows = (room.windows ?? []).map((w, i) =>
      i === windowIndex ? { ...w, ...patch } : w,
    );
    return saveRoomPatch({ windows: nextWindows });
  };

  const handleAddWindow = () => saveRoomPatch({ windows: [...(room.windows ?? []), BLANK_WINDOW] });

  const handleRemoveWindow = (windowIndex: number) =>
    saveRoomPatch({ windows: (room.windows ?? []).filter((_, i) => i !== windowIndex) });

  const handleDeleteRoom = async () => {
    setDeleteError("");
    try {
      const nextRooms = household.rooms.filter((_, i) => i !== roomIndex);
      await updateMutation.mutateAsync({ id: household.id, body: { rooms: nextRooms } });
      setConfirmOpen(false);
    } catch (err) {
      setDeleteError(getApiErrorMessage(err, "Failed to delete room."));
    }
  };

  const handleDeviceChange = (deviceIndex: number, patch: Partial<Device>) => {
    const nextDevices = (room.devices ?? []).map((d, i) =>
      i === deviceIndex ? { ...d, ...patch } : d,
    );
    return saveRoomPatch({ devices: nextDevices });
  };

  const windows = room.windows ?? [];
  const devices = room.devices ?? [];
  const allLinked = devices.every(isDeviceLinked);
  const missingDimensions =
    room.width_m == null || room.length_m == null || room.ceiling_height_m == null;

  return (
    <details
      className="group border-border-subtle bg-card overflow-hidden rounded-md border"
      open={defaultOpen}
    >
      <summary
        className={cn(
          "hover:bg-surface-subtle group-open:bg-surface-subtle group-open:border-border flex cursor-pointer list-none items-center gap-3 border-b border-transparent px-4 py-3.5 [&::-webkit-details-marker]:hidden",
        )}
      >
        <CaretRight
          className="text-muted size-4 shrink-0 transition-transform group-open:rotate-90"
          weight="light"
        />
        <span className="text-primary text-[15.5px] font-bold">
          <InlineEdit
            value={room.name}
            onSave={(v) => saveRoomPatch({ name: v })}
            ariaLabel="Room name"
          >
            {room.name}
          </InlineEdit>
        </span>
        <span className="border-border bg-card text-muted rounded-sm border px-2.5 py-0.5 text-[11px] font-semibold">
          <InlineEdit
            value={room.room_type ?? ""}
            onSave={(v) => saveRoomPatch({ room_type: v || null })}
            ariaLabel="Room type"
            placeholder="Other"
          >
            {room.room_type ?? "Other"}
          </InlineEdit>
        </span>
        {missingDimensions && <MissingChip variant="handoff" />}
        <span className="ml-auto flex items-center gap-3">
          <span className="text-muted flex items-center gap-3 text-[12.5px] tabular-nums group-open:hidden">
            <span className="font-mono">{room.area_m2 ?? "—"} m²</span>
            <span>·</span>
            <span className="font-mono">
              {windows.length} win · {devices.length} dev
            </span>
            <span
              className={cn("size-2 rounded-full", allLinked ? "bg-success" : "bg-warning")}
              title={allLinked ? "All devices linked" : "Some devices need attention"}
            />
          </span>
          <button
            type="button"
            aria-label={`Delete ${room.name || "room"}`}
            title="Delete room"
            onClick={(e) => {
              // Inside <summary>: stop the click from toggling the accordion.
              e.preventDefault();
              e.stopPropagation();
              setDeleteError("");
              setConfirmOpen(true);
            }}
            className="text-muted hover:text-destructive shrink-0 rounded p-1 transition-colors"
          >
            <Trash className="size-4" weight="light" />
          </button>
        </span>
      </summary>

      <div className="flex flex-wrap gap-2 px-4 pt-3.5 pb-1.5">
        <MetricChip
          value={room.area_m2}
          suffix=" m²"
          label="area"
          ariaLabel="Room area in square metres"
          onSave={(v) => saveRoomPatch({ area_m2: v === "" ? null : Number(v) })}
        />
        <div className="bg-background flex items-center gap-1.5 rounded-sm px-2.5 py-1.5 text-[12.5px]">
          <InlineEdit
            value={room.width_m != null ? String(room.width_m) : ""}
            type="number"
            onSave={(v) => saveRoomPatch({ width_m: v === "" ? null : Number(v) })}
            ariaLabel="Room width in metres"
            className="text-primary font-mono font-semibold"
          >
            <b className="text-primary font-mono font-semibold">{room.width_m ?? "—"}</b>
          </InlineEdit>
          <span className="text-muted">×</span>
          <InlineEdit
            value={room.length_m != null ? String(room.length_m) : ""}
            type="number"
            onSave={(v) => saveRoomPatch({ length_m: v === "" ? null : Number(v) })}
            ariaLabel="Room length in metres"
            className="text-primary font-mono font-semibold"
          >
            <b className="text-primary font-mono font-semibold">{room.length_m ?? "—"}</b>
          </InlineEdit>
          <span className="text-muted">m</span>
        </div>
        <MetricChip
          value={room.ceiling_height_m}
          suffix=" m"
          label="ceiling"
          ariaLabel="Ceiling height in metres"
          onSave={(v) => saveRoomPatch({ ceiling_height_m: v === "" ? null : Number(v) })}
        />
        <MetricChip
          value={room.ceiling_slope_deg}
          suffix="°"
          label="slope"
          ariaLabel="Ceiling slope in degrees"
          onSave={(v) => saveRoomPatch({ ceiling_slope_deg: v === "" ? null : Number(v) })}
        />
        <div className="bg-background flex items-center gap-1.5 rounded-sm px-2.5 py-1.5 text-[12.5px]">
          <InlineEdit
            value={
              room.external_wall_factor != null
                ? String(Math.round(room.external_wall_factor * 100))
                : ""
            }
            type="number"
            onSave={(v) =>
              saveRoomPatch({ external_wall_factor: v === "" ? null : Number(v) / 100 })
            }
            ariaLabel="External wall percentage"
            className="text-primary font-mono font-semibold"
          >
            <b className="text-primary font-mono font-semibold">
              {room.external_wall_factor != null
                ? Math.round(room.external_wall_factor * 100)
                : "—"}
            </b>
          </InlineEdit>
          <span className="text-muted">/</span>
          <InlineEdit
            value={
              room.internal_wall_factor != null
                ? String(Math.round(room.internal_wall_factor * 100))
                : ""
            }
            type="number"
            onSave={(v) =>
              saveRoomPatch({ internal_wall_factor: v === "" ? null : Number(v) / 100 })
            }
            ariaLabel="Internal wall percentage"
            className="text-primary font-mono font-semibold"
          >
            <b className="text-primary font-mono font-semibold">
              {room.internal_wall_factor != null
                ? Math.round(room.internal_wall_factor * 100)
                : "—"}
            </b>
          </InlineEdit>
          <span className="text-muted">ext/int walls %</span>
        </div>
        <div className="bg-background flex items-center gap-1.5 rounded-sm px-2.5 py-1.5 text-[12.5px]">
          <InlineEdit
            value={room.floor != null ? String(room.floor) : ""}
            type="number"
            onSave={(v) => saveRoomPatch({ floor: v === "" ? undefined : Number(v) })}
            ariaLabel="Floor number"
            className="text-primary font-mono font-semibold"
          >
            <b className="text-primary font-mono font-semibold">{floorLabel(room.floor)}</b>
          </InlineEdit>
          <span className="text-muted">floor</span>
        </div>
      </div>

      <RoomWindows
        windows={windows}
        onChange={handleWindowChange}
        onAdd={handleAddWindow}
        onRemove={handleRemoveWindow}
      />
      <RoomDevices
        devices={devices}
        onChange={handleDeviceChange}
        airthingsSensorIds={household.preconfig.airthings?.map((s) => s.sensor_id) ?? []}
      />
      <div className="h-2" />

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete room</DialogTitle>
            <DialogDescription>
              Delete <strong>{room.name || "this room"}</strong> and its {windows.length} window
              {windows.length === 1 ? "" : "s"} and {devices.length} device
              {devices.length === 1 ? "" : "s"}? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {deleteError && (
            <p role="alert" className="text-destructive text-sm">
              {deleteError}
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleDeleteRoom()}
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending ? (
                <CircleNotch className="h-4 w-4 animate-spin" weight="light" />
              ) : (
                "Delete room"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </details>
  );
}
